import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/hooks/use-language';
import { usePageHero } from '@/hooks/usePageHero';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { usefulData } from '@/lib/useful-data';
import { SEO } from '@/components/ui/SEO';
import { buildBreadcrumbs } from '@/lib/schema';
import { 
  buildUsefulHubPath, 
  buildUsefulArticlePath, 
  getUsefulRootPath, 
  resolveHubIdFromSlug, 
  getUsefulHubSlug,
  type Lang 
} from '@/lib/localizedRoutes';
import { DarkNotesSection } from '@/components/useful/DarkNotesSection';
import { ChevronRight } from 'lucide-react';

export const Useful: React.FC<{ seoEnabled?: boolean }> = ({ seoEnabled = true }) => {
  const { language, t } = useLanguage();
  const usefulHero = usePageHero({
    pageKey: 'useful',
    lang: language,
    fallbacks: {
      eyebrow: t('useful.knowledge_base'),
      title: t('nav.useful'),
    },
  });
  const { hub: hubSlug } = useParams<{ hub?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const hubs = ['spc-laminat', 'spc-paneli', 'carpet-tile'];
  
  // Swipe logic
  const touchStart = useRef<{ x: number, y: number } | null>(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart.current || !hubSlug) return;
    
    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    const deltaX = touchStart.current.x - touchEnd.x;
    const deltaY = touchStart.current.y - touchEnd.y;

    // Only trigger if horizontal swipe is dominant and significant
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
      const currentIndex = hubs.indexOf(hubSlug);
      if (currentIndex !== -1) {
        if (deltaX > 0 && currentIndex < hubs.length - 1) {
          // Swipe left -> Next hub
          navigate(buildUsefulHubPath(language as Lang, hubs[currentIndex + 1]));
        } else if (deltaX < 0 && currentIndex > 0) {
          // Swipe right -> Previous hub
          navigate(buildUsefulHubPath(language as Lang, hubs[currentIndex - 1]));
        }
      }
    }
    touchStart.current = null;
  };

  const currentHub = useMemo(() => {
    if (!hubSlug) return null;
    const hubId = resolveHubIdFromSlug(hubSlug, language as Lang);
    return usefulData.find(h => h.id === (hubId || hubSlug));
  }, [hubSlug, language]);

  // If no hub, we are on /useful (The Hub)
  const isMainHub = !hubSlug;

  // SEO metadata
  const hubTranslations = currentHub?.translations?.[language] || currentHub?.translations?.ru;
  const pageTitle = currentHub?.seoTitle || hubTranslations?.title || currentHub?.title || t('page.useful.title');
  const pageDescription = currentHub?.seoDescription || hubTranslations?.description || currentHub?.description || t('useful.subtitle');

  const hubLabel = currentHub
    ? (hubTranslations?.title || currentHub.title)
    : (language === 'uz' ? 'Foydali' : language === 'en' ? 'Useful' : 'Полезное');
  const schema = buildBreadcrumbs(window.location.origin, [
    { name: language === 'uz' ? 'Bosh sahifa' : language === 'en' ? 'Home' : 'Главная', url: `/${language}` },
    ...(isMainHub
      ? [{ name: hubLabel, url: getUsefulRootPath(language as Lang) }]
      : [
          { name: language === 'uz' ? 'Foydali' : language === 'en' ? 'Useful' : 'Полезное', url: getUsefulRootPath(language as Lang) },
          { name: hubLabel, url: buildUsefulHubPath(language as Lang, currentHub?.id || hubSlug || '') },
        ]
    ),
  ]);

  const headerVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  const ProductSwitcher = () => (
    <div className={cn(
      "sticky top-[60px] lg:top-[72px] z-30 bg-[#13100B]/95 backdrop-blur-md border-y border-white/5 py-3 md:py-4 mb-4 lg:mb-12 transition-all",
      isMobile ? "top-[60px] mb-4 bg-background/95" : "top-[72px] mb-12"
    )}>
      <div className="container-custom">
        <div className={cn(
          "flex items-center gap-2 md:gap-8 overflow-x-auto no-scrollbar scroll-smooth",
          isMobile && "grid grid-cols-3 gap-1 overflow-x-hidden justify-center",
          !isMobile && "justify-start"
        )}>
          {usefulData.map((hub) => {
            const isActive = currentHub?.id === hub.id;
            const hubTrans = hub.translations?.[language] || hub.translations?.ru;
            return (
              <Link
                key={hub.id}
                to={buildUsefulHubPath(language as Lang, hub.id)}
                className={cn(
                  "whitespace-nowrap lg:whitespace-nowrap text-[8px] sm:text-[9px] md:text-xs font-black uppercase tracking-[0.1em] md:tracking-[0.2em] transition-all relative py-3 px-1 sm:px-2 md:px-6 rounded-sm border flex items-center justify-center text-center leading-tight",
                  isMobile && "whitespace-normal",
                  isActive 
                    ? "text-primary border-primary/30 bg-primary/5 shadow-[0_0_15px_rgba(176,138,90,0.1)]" 
                    : "text-white/30 border-white/5 hover:text-white hover:border-white/10"
                )}
              >
                {hubTrans ? hubTrans.title : hub.title}
                {isActive && (
                  <motion.div 
                    layoutId="useful-hub-indicator"
                    className="absolute -top-[1px] -left-[1px] w-1.5 h-1.5 border-t border-l border-primary"
                  />
                )}
                {isActive && (
                  <motion.div 
                    layoutId="useful-hub-indicator-br"
                    className="absolute -bottom-[1px] -right-[1px] w-1.5 h-1.5 border-b border-r border-primary"
                  />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div 
      className="pb-16 lg:pb-24 relative overflow-x-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Continuous grid background for entire /useful page */}
      <div className="grid-cells opacity-50 fixed inset-0 pointer-events-none" style={{ zIndex: 0 }} />
      
      <SEO 
        enabled={seoEnabled}
        title={pageTitle}
        description={pageDescription}
        canonical={isMainHub ? getUsefulRootPath(language as Lang) : buildUsefulHubPath(language as Lang, currentHub?.id || hubSlug || '')}
        schema={schema}
      />

      {isMobile && !isMainHub && <ProductSwitcher />}

      {/* Header */}
      <section className={cn(
        "pt-24 md:pt-32 pb-12 lg:pb-16 relative",
        isMobile && !isMainHub && "pt-8" // Reduce padding if switcher is at top
      )}>
        <div className="container-custom relative z-10">
          
          <div className="space-y-4 lg:space-y-6">
            {usefulHero.eyebrow?.trim() && (
              <motion.div 
                custom={0}
                initial="hidden"
                animate="visible"
                variants={headerVariants}
                className="flex items-center gap-2 lg:gap-3"
              >
                <div className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                <p className="text-primary font-black uppercase tracking-[0.4em] text-[10px]">{usefulHero.eyebrow}</p>
              </motion.div>
            )}
            
            {usefulHero.title?.trim() && (
              <motion.h1 
                custom={1}
                initial="hidden"
                animate="visible"
                variants={headerVariants}
                className="font-black uppercase tracking-tighter leading-[0.95] lg:leading-[0.8] max-w-4xl"
              >
                {usefulHero.title}<span className="text-primary">.</span>
              </motion.h1>
            )}
            
            {usefulHero.subtitle?.trim() && (
              <motion.p 
                custom={2}
                initial="hidden"
                animate="visible"
                variants={headerVariants}
                className="text-white/40 max-w-xl text-sm md:text-base lg:text-lg leading-relaxed font-black uppercase tracking-wider"
              >
                {usefulHero.subtitle}
              </motion.p>
            )}
          </div>
        </div>
      </section>

      {/* Product Switcher (Desktop) */}
      {!isMainHub && !isMobile && <ProductSwitcher />}

      <section className="relative min-h-[50vh]">
        <div className="container-custom relative z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={hubSlug || 'main-hub'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {!isMainHub && (
                <div className="mb-16 space-y-12">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 bg-primary shadow-[0_0_10px_rgba(176,138,90,1)]"></div>
                    <h2 className="text-xs font-black uppercase tracking-[0.4em] text-white/40">{t('useful.section_articles')}</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentHub?.articles.map((article, idx) => {
                      const content = article.translations[language] || article.translations.ru;
                      return (
                        <Link
                          key={article.id}
                          to={buildUsefulArticlePath(language as Lang, currentHub.id, article.id)}
                          className={cn(
                            "mirror-card p-8 group hover:border-primary/30 transition-all duration-500 flex flex-col justify-between min-h-[240px] relative overflow-hidden",
                            isMobile && "bg-[#13100B] border-white/10" // Force color on mobile/tablet
                          )}
                        >
                          <div className="absolute top-0 right-0 p-6 text-white/5 font-mono text-4xl font-black group-hover:text-primary/10 transition-colors">
                            0{idx + 1}
                          </div>
                          <div className="space-y-4 relative z-10">
                            <div className="text-primary text-[9px] uppercase tracking-[0.3em] font-bold">{t('useful.read_more')}</div>
                            <h3 className="text-xl font-extrabold uppercase tracking-tight leading-tight group-hover:text-primary transition-colors duration-500">
                              {content.title}
                            </h3>
                          </div>
                          <div className="flex items-center justify-between pt-8 relative z-10">
                            <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest group-hover:text-white transition-colors">{t('useful.read_article')}</p>
                            <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center group-hover:border-primary/50 group-hover:bg-primary/10 transition-all duration-500">
                              <ChevronRight size={16} className="text-white/30 group-hover:text-primary transition-colors" />
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {isMainHub && (
                <DarkNotesSection 
                  notes={usefulData.map((hub, index) => {
                    const hubTrans = hub.translations?.[language] || hub.translations?.ru;
                    return {
                      id: hub.id,
                      number: `0${index + 1}`,
                      title: hubTrans ? hubTrans.title : hub.title,
                      description: hubTrans ? hubTrans.description : hub.description,
                      link: buildUsefulHubPath(language as Lang, hub.id),
                      rotation: index === 0 ? -2 : index === 1 ? 2 : -1
                    };
                  })}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
};

